/**
 * Stock Alert Component
 * Displays list of stock alerts with search and filter capabilities
 */

import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  inject,
  ChangeDetectionStrategy,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, Observable, from, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { map, catchError, switchMap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { AlertManagementService } from '@app/api/stock-service';
import { AlertStatusUpdateDTO } from '@app/api/stock-service/model/alertStatusUpdateDTO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { PermissionService } from '@app/core/services/permission.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { environment } from '@environments/environment';
import {
  AlertReasonDialogComponent,
  AlertReasonDialogData,
  AlertReasonDialogResult,
} from './components/alert-reason-dialog/alert-reason-dialog.component';

import {
  STOCK_ALERT_SEARCH_CONFIG,
  STOCK_ALERT_TABLE_CONFIG,
  STOCK_ALERT_DEFAULT_CONFIG,
  STOCK_ALERT_DEFAULT_COLUMNS_ORDER,
  STOCK_ALERT_FLEXIBLE_COLUMNS_ORDER,
  STOCK_ALERT_ACTION_COLUMN,
} from './stock-alert.config';

// Types
export type AlertType = 'LOW_STOCK' | 'OVER_STOCK';
export type AlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AlertStatus = 'NEW' | 'RESOLVED' | 'SUPPRESSED';

export interface StockAlert {
  id: number;
  skuCode: string;
  skuName: string;
  type: string;
  severity: string;
  status: string;
  warehouseCode?: string;
  warehouseName?: string;
  currentStock?: number;
  creationDate: string;
}

export interface AlertPageRequest {
  page: number;
  size: number;
  sortBy?: string;
  ascending?: boolean;
  skuCode?: string;
  warehouseCode?: string;
  types?: AlertType[];
  severities?: AlertSeverity[];
  statuses?: AlertStatus[];
}

export interface AlertPageResponse {
  pageContent: StockAlert[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
  numberOfElements: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Severity color configuration
const SEVERITY_COLORS: Record<
  string,
  Record<string, { bg: string; text: string }>
> = {
  LOW_STOCK: {
    LOW: { bg: '#FACC15', text: '#FFFFFF' },
    MEDIUM: { bg: '#FB923C', text: '#FFFFFF' },
    HIGH: { bg: '#F97316', text: '#FFFFFF' },
    CRITICAL: { bg: '#DC2626', text: '#FFFFFF' },
  },
  OVER_STOCK: {
    LOW: { bg: '#93C5FD', text: '#FFFFFF' },
    MEDIUM: { bg: '#3B82F6', text: '#FFFFFF' },
    HIGH: { bg: '#1D4ED8', text: '#FFFFFF' },
    CRITICAL: { bg: '#6D28D9', text: '#FFFFFF' },
  },
};

// Status severity mapping for PrimeNG Tag
const STATUS_SEVERITY: Record<
  string,
  'success' | 'warning' | 'danger' | 'info' | 'secondary'
> = {
  NEW: 'info',
  RESOLVED: 'success',
  SUPPRESSED: 'secondary',
};

@Component({
  selector: 'app-stock-alert',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TagModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AlertReasonDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './stock-alert.component.html',
  styleUrl: './stock-alert.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockAlertComponent implements OnInit, OnDestroy {
  private readonly httpClient = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly alertManagementService = inject(AlertManagementService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private readonly destroy$ = new Subject<void>();

  // Permission service
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'stock_alert' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.stock_alert.view');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.stock_alert.approve');
  });

  // Configuration
  searchConfig = signal(this.buildSearchConfig());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_ALERT_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of STOCK_ALERT_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Conditionally add actions column at the end if permitted
    if (this.canApprove()) {
      const actionsCol = fromConfig(STOCK_ALERT_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_ALERT_TABLE_CONFIG.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of STOCK_ALERT_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  pageSize = STOCK_ALERT_DEFAULT_CONFIG.PAGE_SIZE;

  // State management
  alerts = signal<StockAlert[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: STOCK_ALERT_DEFAULT_CONFIG.PAGE_SIZE,
  });
  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Reason dialog state
  showReasonDialog = signal(false);
  reasonDialogData = signal<AlertReasonDialogData | null>(null);

  // Search criteria
  private searchCriteria: Partial<AlertPageRequest> = {};

  ngOnInit(): void {
    this.loadData();
  }

  private buildSearchConfig(): FormFieldConfig[] {
    return withPaginatedSearchFields(STOCK_ALERT_SEARCH_CONFIG, {
      skuCode: {
        loadPage: (params) => this.skuOptionsService.getOptionsPage(params),
      },
      warehouseCode: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            valueKey: 'code',
          }),
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    const params: Partial<AlertPageRequest> = {};

    const skuCode = (searchData['skuCode'] as string)?.trim();
    if (skuCode) params.skuCode = skuCode;

    const warehouseCode = (searchData['warehouseCode'] as string)?.trim();
    if (warehouseCode) params.warehouseCode = warehouseCode;

    if (
      searchData['types'] &&
      Array.isArray(searchData['types']) &&
      searchData['types'].length > 0
    ) {
      params.types = searchData['types'] as AlertType[];
    }

    if (
      searchData['severities'] &&
      Array.isArray(searchData['severities']) &&
      searchData['severities'].length > 0
    ) {
      params.severities = searchData['severities'] as AlertSeverity[];
    }
    if (
      searchData['statuses'] &&
      Array.isArray(searchData['statuses']) &&
      searchData['statuses'].length > 0
    ) {
      params.statuses = searchData['statuses'] as AlertStatus[];
    }

    this.searchCriteria = params;
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadData();
  }

  onReset(): void {
    this.searchCriteria = {};
    this.searchParams.set({
      page: 1,
      size: STOCK_ALERT_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.loadData();
  }

  onPageChange(event: any): void {
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  /**
   * Get severity colors (background and text) based on alert type and severity level
   */
  getSeverityColors(
    type: string,
    severity: string,
  ): { bg: string; text: string } {
    const typeColors = SEVERITY_COLORS[type];
    if (typeColors?.[severity]) {
      return typeColors[severity];
    }
    return { bg: '#E5E7EB', text: '#374151' }; // Default gray
  }

  /**
   * Get status severity for PrimeNG Tag component
   */
  getStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
    return STATUS_SEVERITY[status] || 'secondary';
  }

  /**
   * Check if alert can be resolved (only NEW status can be resolved)
   */
  canResolve(alert: StockAlert): boolean {
    return alert.status === 'NEW';
  }

  /**
   * Check if alert can be suppressed (only NEW status can be suppressed)
   */
  canSuppress(alert: StockAlert): boolean {
    return alert.status === 'NEW';
  }

  /**
   * Update alert status to RESOLVED
   */
  onResolve(alert: StockAlert): void {
    this.reasonDialogData.set({
      alertId: alert.id,
      action: 'RESOLVED',
      alertInfo: {
        skuCode: alert.skuCode,
        skuName: alert.skuName,
      },
    });
    this.showReasonDialog.set(true);
  }

  /**
   * Update alert status to SUPPRESSED
   */
  onSuppress(alert: StockAlert): void {
    this.reasonDialogData.set({
      alertId: alert.id,
      action: 'SUPPRESSED',
      alertInfo: {
        skuCode: alert.skuCode,
        skuName: alert.skuName,
      },
    });
    this.showReasonDialog.set(true);
  }

  /**
   * Handle reason dialog result
   */
  onReasonDialogResult(result: AlertReasonDialogResult): void {
    if (result.confirmed && result.reason && this.reasonDialogData()) {
      const data = this.reasonDialogData()!;
      this.updateAlertStatus(data.alertId, data.action, result.reason);
    }
    this.showReasonDialog.set(false);
    this.reasonDialogData.set(null);
  }

  /**
   * Update alert status via API
   */
  private updateAlertStatus(
    alertId: number,
    status: 'RESOLVED' | 'SUPPRESSED',
    resolvedReason: string,
  ): void {
    const updateDTO: AlertStatusUpdateDTO = {
      id: alertId,
      status: status as AlertStatusUpdateDTO.StatusEnum,
      resolvedReason: resolvedReason,
    };

    this.alertManagementService
      .updateAlertStatus(updateDTO, 'body')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'stock_alert.messages.status_updated',
            ),
          });
          // Reload data to reflect the change
          this.loadData();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'stock_alert.messages.failed_to_update_status',
            ),
          });
        },
      });
  }

  formatType(type: string): string {
    const typeKey = type.toLowerCase();
    return this.translateService.instant(`stock_alert.type.${typeKey}`);
  }

  formatSeverity(severity: string): string {
    return this.translateService.instant(
      `stock_alert.severity.${severity.toLowerCase()}`,
    );
  }

  formatStatus(status: string): string {
    return this.translateService.instant(
      `stock_alert.status.${status.toLowerCase()}`,
    );
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('vi-VN');
  }

  /**
   * Map warehouseCode to warehouseName from loaded warehouse options
   */
  private mapWarehouseCodeToName(alerts: StockAlert[]): StockAlert[] {
    return alerts.map((alert) => {
      if (alert.warehouseCode && !alert.warehouseName) {
        alert.warehouseName = alert.warehouseCode;
      }
      return alert;
    });
  }

  private loadData(): void {
    this.loading.set(true);

    const requestBody: AlertPageRequest = {
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
      size: this.searchParams().size,
      sortBy: 'creationDate',
      ascending: false,
      ...this.searchCriteria,
    };

    this.pageAlerts(requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const alerts = response.pageContent || [];
          // Map warehouseCode to warehouseName
          const mappedAlerts = this.mapWarehouseCodeToName(alerts);
          this.alerts.set(mappedAlerts);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading alerts:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'stock_alert.messages.failed_to_load',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  /**
   * Call API POST /v1/alerts/page
   */
  private pageAlerts(
    requestBody: AlertPageRequest,
  ): Observable<AlertPageResponse> {
    const basePath =
      environment.stockURL || environment.stockServiceBasePath || '';
    const url = `${basePath}/v1/alerts/page`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // Use HttpRequest to support POST with body
    const request = new HttpRequest<AlertPageRequest>(
      'POST',
      url,
      requestBody,
      {
        headers: headers,
        reportProgress: false,
        responseType: 'json',
      },
    );

    return this.httpClient.request<AlertPageResponse>(request).pipe(
      switchMap((event: any) => {
        // Handle HttpResponse
        if (event instanceof HttpResponse) {
          let response = event.body;
          // Handle Blob response
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response)).pipe(
              map((parsedResponse) =>
                this.mapApiResponseToAlertPageResponse(parsedResponse || {}),
              ),
            );
          }
          return of(this.mapApiResponseToAlertPageResponse(response || {}));
        }
        // If not HttpResponse, try to handle as direct response
        if (event && typeof event === 'object') {
          if (BlobConverterUtil.isBlob(event)) {
            return from(BlobConverterUtil.convertToJson(event)).pipe(
              map((parsedResponse) =>
                this.mapApiResponseToAlertPageResponse(parsedResponse || {}),
              ),
            );
          }
          return of(this.mapApiResponseToAlertPageResponse(event));
        }
        return of(event);
      }),
      catchError((error) => {
        console.error('API Error:', error);
        // Don't show error message here as it's already shown in loadData error handler
        return of({
          pageContent: [],
          number: 0,
          size: 0,
          totalElements: 0,
          totalPages: 0,
          empty: true,
          numberOfElements: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        } as AlertPageResponse);
      }),
    ) as Observable<AlertPageResponse>;
  }

  /**
   * Map API response to AlertPageResponse format
   */
  private mapApiResponseToAlertPageResponse(response: any): AlertPageResponse {
    return {
      pageContent: response.pageContent || [],
      number: response.number ?? 0,
      size: response.size ?? 0,
      totalElements: response.totalElements ?? 0,
      totalPages: response.totalPages ?? 0,
      empty: response.empty ?? false,
      numberOfElements: response.numberOfElements ?? 0,
      hasNextPage: response.hasNextPage ?? false,
      hasPreviousPage: response.hasPreviousPage ?? false,
    };
  }
}
